/**
 * MetaConnectionStatus
 * 
 * Displays status of connected Meta (Facebook + Instagram) accounts.
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, X, RefreshCw, Unplug, Loader2, Lock, Share2, HelpCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { EmptyState } from '../ui/empty-state';
import { fetchWithAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social Media Icons (SVG) - lucide-react doesn't have these
const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

export default function MetaConnectionStatus({ onRefresh }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { toast } = useToast();

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/social/meta/accounts');

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();

      if (data.success) {
        setAccounts(data.data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      toast({
        title: 'Error',
        description: 'Failed to load connected accounts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleVerify = async (tokenId) => {
    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/meta/verify?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Account verified successfully',
          variant: 'default'
        });
        fetchAccounts();
      } else {
        toast({
          title: 'Verification Failed',
          description: data.message,
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Verification failed',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (tokenId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) {
      return;
    }

    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/meta/disconnect?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Disconnect failed');
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Account disconnected successfully',
          variant: 'default'
        });
        fetchAccounts();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect account',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefreshMetadata = async (tokenId) => {
    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/meta/refresh?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Metadata refreshed successfully',
          variant: 'default'
        });
        fetchAccounts();
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to refresh metadata',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6" data-testid="meta-connection-status-loading">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          <span className="ml-2 text-gray-400">Loading accounts...</span>
        </div>
      </Card>
    );
  }

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={<Lock size={48} />}
        title="Connectez vos réseaux sociaux 🌐"
        message="Liez vos comptes Facebook, Instagram, LinkedIn ou TikTok pour publier du contenu directement depuis NEXORA AI. OAuth sécurisé, données protégées."
        primaryAction={{
          label: "Connecter Meta (Facebook + Instagram)",
          onClick: () => window.location.href = '/social-accounts',
          icon: <Share2 size={20} />
        }}
        secondaryAction={{
          label: "Pourquoi connecter mes comptes ?",
          onClick: () => {
            toast({
              title: "OAuth Sécurisé",
              description: "Vos identifiants restent privés. NEXORA AI utilise OAuth 2.0 pour une connexion sécurisée sans stocker vos mots de passe.",
              variant: "default"
            });
          },
          variant: "outline",
          icon: <HelpCircle size={20} />
        }}
        variant="info"
        size="default"
        animated={true}
        testId="empty-social-accounts"
        footer={
          <p className="text-sm text-zinc-500">
            🔒 Vos données sont protégées par OAuth 2.0 — NEXORA AI ne stocke jamais vos mots de passe
          </p>
        }
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="meta-connection-status">
      {accounts.map((account) => {
        const Icon = account.platform === 'facebook' ? FacebookIcon : InstagramIcon;
        const isLoading = actionLoading === account.token_id;

        return (
          <Card
            key={account.token_id}
            className="p-4 bg-gray-900/50 border-gray-800"
            data-testid={`meta-account-${account.platform}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="p-2 rounded-lg bg-gray-800">
                  <Icon className="w-5 h-5 text-purple-400" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-white" data-testid="account-name">
                      {account.account_name}
                    </h3>
                    {account.is_active ? (
                      <Badge variant="success" className="text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">
                        <X className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-400 mt-1" data-testid="account-type">
                    {account.platform === 'facebook' ? 'Facebook Page' : 'Instagram Business'}
                  </p>
                  
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    {account.can_publish && (
                      <span className="flex items-center">
                        <Check className="w-3 h-3 mr-1 text-green-500" />
                        Can publish
                      </span>
                    )}
                    {account.can_read_analytics && (
                      <span className="flex items-center">
                        <Check className="w-3 h-3 mr-1 text-green-500" />
                        Analytics
                      </span>
                    )}
                    {account.last_verified && (
                      <span>
                        Verified: {new Date(account.last_verified).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleVerify(account.token_id)}
                  disabled={isLoading}
                  data-testid="verify-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRefreshMetadata(account.token_id)}
                  disabled={isLoading}
                  data-testid="refresh-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDisconnect(account.token_id)}
                  disabled={isLoading}
                  className="text-red-400 hover:text-red-300"
                  data-testid="disconnect-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unplug className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
